import { ReactElement } from "react";
import { Box } from "@mui/material";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { usePortfolioGroupInfo } from "../../usePortfolioGroupInfo";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarFlatPageControl } from "@app/components/FlatPageControl";
import {
  usePortfolioGroupVersionData,
  UsePortfolioGroupVersionDataParams,
} from "../../usePortfolioGroupVersionData";
import { CarSelectOption, CarSelectOptionField } from "@app/components/Inputs";
import { usePortfolioPortfolioGroupVersionModulesDataMakeSelectedUpdateMutation } from "@app/api/carApi.generated";
import { AnalyzeModuleSummary } from "./AnalyzeModuleSummary";
import { AnalyzeModulePerformanceRisk } from "./AnalyzeModulePerformanceRisk";
import { AnalyzeModuleMultiPeriodReturn } from "./AnalyzeModuleMultiPeriodReturn";
import { AnalyzeModuleSimulatedForwardProbabilities } from "./AnalyzeModuleSimulatedForwardProbabilities";
import { AnalyzeModuleSectorAnalysis } from "./AnalyzeModuleSectorAnalysis";
import { AnalyzeModuleScenarioAnalysis } from "./AnalyzeModuleScenarioAnalysis";
import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { AnalyzeSummary_assetClassLevel } from "./AnalyzeSummary";
import {
  checkDefined,
  // , openInNewTab
} from "@app/utils";
// import { CarDownloadButton } from "@app/components/Buttons";
// import { getAnalyzeModuleChartDataSelectorSavedValue } from "./useAnalyzeModuleChartDataSelector";
// import { assetAllocationPages } from "route.const";

interface AnalyzeModuleProps {
  reportFocusSelector: ReactElement;
  moduleId: string;
  moduleIdx: number;
  isReadOnly?: boolean;
}

export const AnalyzeModule = (props: AnalyzeModuleProps) => {
  const {
    portfolioGroupId,
    portfolioGroupVersionId,
    isViewPortfolioGroupVersionMode,
  } = usePortfolioGroupParams();

  const { moduleId, moduleIdx } = props;

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const params: UsePortfolioGroupVersionDataParams =
    isViewPortfolioGroupVersionMode
      ? {
          versionId: portfolioGroupVersionId,
          publishedVersionId: undefined,
          hideCash: true,
          isPublishedOnly: true,
          show: {
            benchmark: true,
            existing: false,
            draft: true,
            selected: false,
          },
          filterByModuleId: moduleId,
        }
      : {
          versionId: groupInfo.portfolioGroupVersionId,
          publishedVersionId: groupInfo.publishedVersionId,
          filterByModuleId: moduleId,
          hideCash: true,
        };

  const data = usePortfolioGroupVersionData(params);

  const [currentTab, setCurrentTab] = useLocalStorage<string | undefined>(
    "AnalyzeModule_currentTab",
    "summary",
  );

  // YS todo check why this is commented out
  // also report is not working properly, todo fix report

  // const handleShowPdf = () => {
  //   const chartDataSelector = getAnalyzeModuleChartDataSelectorSavedValue(
  //     portfolioGroupVersionId,
  //   );

  //   if (
  //     portfolioGroupId &&
  //     portfolioGroupVersionId &&
  //     moduleId &&
  //     chartDataSelector.data1Id &&
  //     chartDataSelector.data2Id
  //   ) {
  //     openInNewTab(
  //       assetAllocationPages.getPdfPortfolioGroupModuleReportUrl({
  //         portfolioGroupId,
  //         portfolioGroupVersionId,
  //         moduleId,
  //         data1Id: chartDataSelector.data1Id,
  //         data2Id: chartDataSelector.data2Id,
  //       }),
  //     );
  //   }
  // };

  const [selectModuleData] =
    usePortfolioPortfolioGroupVersionModulesDataMakeSelectedUpdateMutation();

  const handleSelectedModuleDataChange = async (params: {
    moduleId: string;
    dataId: string;
  }) =>
    selectModuleData({
      portfolioGroupVersionId: checkDefined(portfolioGroupVersionId),
      moduleId: params.moduleId,
      id: params.dataId,
    });

  const currentModuleBand = data.tableData.bands.find(
    (i) => i.moduleId === moduleId,
  );

  const selectionItems =
    currentModuleBand?.columns
      .filter((i) => !!i.dataId)
      .map<CarSelectOption<string>>((i) => ({
        value: i.dataId,
        label: i.label,
      })) ?? [];

  return (
    <TablePlaceHolder
      sx={{ mt: 5 }}
      isLoading={groupInfo.isLoading || data.isLoading}
      rows={8}
    >
      {!groupInfo.isSinglePortfolioMode && (
        <Box sx={{ mt: 5, display: "flex", gap: 3.5 }}>
          {props.reportFocusSelector}
          {!props.isReadOnly && (
            <CarSelectOptionField
              label={
                <>
                  Selection for Publish
                  <CarTooltipBox
                    sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                    className={
                      pendoClasses.editPortfolioAnalyzeSelectionForPublish
                    }
                  />
                </>
              }
              sx={{
                ml: "auto",
                width: 200,
                ".MuiInputLabel-root": {
                  fontSize: "16px",
                },
                ".MuiOutlinedInput-root": {
                  mt: 3,
                },
                ".MuiInputBase-input": {
                  backgroundColor: "gray1",
                },
              }}
              options={selectionItems}
              value={currentModuleBand?.selectedDataId ?? ""}
              onChange={(dataId) => {
                if (dataId && currentModuleBand) {
                  handleSelectedModuleDataChange({
                    moduleId: currentModuleBand.moduleId,
                    dataId,
                  });
                }
              }}
            />
          )}
        </Box>
      )}
      {/* <CarDownloadButton
        onClick={handleShowPdf}
        sx={{ zIndex: 1, mt: 2, mr: -1, mb: -2, alignSelf: "flex-end" }}
      >
        Download Reports
      </CarDownloadButton> */}
      <CarFlatPageControl
        sx={{
          mt: 4,
          ".flat-page-control-header": {
            // ml: 3,
            // gap: 3,
            justifyContent: "space-around",
          },
          ".flat-page-control-button": {
            p: 1,
            fontSize: "18px",
          },
        }}
        items={[
          {
            label: "Summary",
            value: "summary",
            content: (
              <AnalyzeModuleSummary
                versionParams={params}
                assetClassLevelStorageKey={AnalyzeSummary_assetClassLevel}
                moduleId={moduleId}
              />
            ),
          },
          {
            label: "Performance Risk",
            value: "performance-risk",
            content: (
              <AnalyzeModulePerformanceRisk
                versionParams={params}
                moduleId={moduleId}
                moduleIdx={moduleIdx}
              />
            ),
          },
          {
            label: "Multi-Period Return",
            value: "multi-period-return",
            content: (
              <AnalyzeModuleMultiPeriodReturn
                versionParams={params}
                moduleId={moduleId}
                moduleIdx={moduleIdx}
              />
            ),
          },
          {
            label: "Simulated Forward Probabilities",
            value: "simulated-forward-probabilities",
            content: (
              <AnalyzeModuleSimulatedForwardProbabilities
                versionParams={params}
                moduleId={moduleId}
              />
            ),
          },
          {
            label: "Scenario Analysis",
            value: "scenario-analysis",
            content: (
              <AnalyzeModuleScenarioAnalysis
                versionParams={params}
                moduleId={moduleId}
              />
            ),
          },
          {
            label: "Sector Analysis",
            value: "sector-analysis",
            content: (
              <AnalyzeModuleSectorAnalysis
                versionParams={params}
                moduleId={moduleId}
                moduleIdx={moduleIdx}
              />
            ),
          },
        ]}
        value={currentTab}
        onChange={setCurrentTab}
      />
    </TablePlaceHolder>
  );
};
